import { DateHelper } from '@loadsmart/loadsmart-ui'
import moment from 'moment'

import {
  endOfDayUTC,
  startOfDayUTC,
  utcToDisplayDate,
  utcToMilitaryDisplayTime,
} from '../../util/datetime'
import { ROW_TRUCK_FUEL_TYPE_OPTIONS, TRUCK_CLASS_OPTIONS } from '../constants/reports'

export function formatDateTime(time: string, format = 'MM/DD/YYYY - hh:mm A') {
  return moment(time).format(format)
}

export function formatDate(time: string, format = 'MM/DD/YYYY') {
  return moment(time).format(format)
}

export function formatTime(time: string, format = 'hh:mm A') {
  return moment(time).format(format)
}

export function getTruckClass(truckClass: string) {
  return TRUCK_CLASS_OPTIONS.find((option) => option.value === truckClass)?.label || '-'
}

export function getTruckFuelType(fuelType: string) {
  return ROW_TRUCK_FUEL_TYPE_OPTIONS.find((option) => option.value === fuelType)?.label || '-'
}

export const getAssetDwellTime = (dwellTime?: number) => {
  if (dwellTime) {
    dwellTime = Number(dwellTime)
    const d = Math.floor(dwellTime / (3600 * 24))
    const h = Math.floor((dwellTime % (3600 * 24)) / 3600)
    const m = Math.floor((dwellTime % 3600) / 60)

    const dDisplay = d > 0 ? d + (d == 1 ? ' day, ' : ' days, ') : ''
    const hDisplay = h > 0 ? h + (h == 1 ? ' hr, ' : ' hrs, ') : ''
    const mDisplay = d === 0 && h === 0 && m === 0 ? '0 mins' : m + (m == 1 ? ' min ' : ' mins ')

    return dDisplay + hDisplay + mDisplay
  }
  return '-'
}

export function getRowValue(rowType: string, row): any {
  switch (rowType) {
    case 'assetType':
      if (row?.attachmentName) {
        return row.attachmentName
      }
      if (row?.assetName) {
        return row.assetName
      }
      if (row?.vehicleTypeName) {
        return row?.vehicleTypeName
      }
      return 'Unkown'
    case 'DATE':
      return utcToDisplayDate(row?.detectedAt, row?.timezone)
    case 'TIME':
      return utcToMilitaryDisplayTime(row?.detectedAt, row?.timezone)
    case 'ingateTime':
      return formatDateTime(row?.detectedAt)
    case 'assetDirection':
      return row?.direction ? 'ingate' : 'outgate'
    case 'loadType':
      return row?.loadType || '-'
    case 'loadNumber':
      return row?.loadNumber || '-'
    case 'hasSeal':
      if (row?.hasSeal == null) {
        return '-'
      }
      return row?.hasSeal ? 'Yes' : 'No'
    case 'sealNumber':
      return row?.sealNumber || '-'
    case 'loadLicensePlate':
      return row?.loadLicensePlate || '-'
    case 'loadDamage':
      return row?.loadDamage || '-'
    case 'loadRemarks':
      return row?.loadRemarks || '-'
    case 'chassisNumber':
      return row?.chassisNumber || '-'
    case 'chassisLicensePlate':
      return row?.chassisLicensePlate || '-'
    case 'chassisDamage':
      return row?.chassisDamage || '-'
    case 'chassisRemarks':
      return row?.chassisRemarks || '-'
    case 'vehicleCarrierName':
      return row?.carrierName || row?.vehicle?.carrier?.name || '-'
    case 'vehicleNumber':
      return row?.vehicleNumber || '-'
    case 'vehicleRemarks':
      return row?.vehicleRemarks || '-'
    case 'vehicleFuelType':
      return getTruckFuelType(row?.vehicleFuelType)
    case 'vehicleDotNumber':
      return row?.vehicleDotNumber || '-'
    case 'vehicleLicensePlate':
      return row?.vehicleLicensePlate || '-'
    case 'vehicleDamage':
      return row?.vehicleDamage || '-'
    case 'vehicleWeightClass':
      return getTruckClass(row?.vehicleWeightClass)
    case 'vehicleDwellTimeHr':
      if (row?.vehicleId) {
        return getAssetDwellTime(row?.vehicleDwellTime)
      }
      if (row?.chassisId) {
        return getAssetDwellTime(row?.chassisDwellTime)
      }
      if (row?.loadId) {
        return getAssetDwellTime(row?.loadDwellTime)
      }
    default:
      return null
  }
}

export function getAllowedSites(user, locations) {
  let allowedSites = []

  if (locations) {
    Object.keys(locations).forEach((location) => {
      const locationData = locations[location]
      allowedSites.push({
        ...locationData,
        label: locationData.name,
        value: locationData.id,
      })
    })
    if (user && user.meta.sites) {
      allowedSites = allowedSites.filter((site) => user.meta.sites.includes(site.alias))
    }
  }
  return allowedSites
}

export function getFormInitialValues(
  assetData?: AssetData,
  sites?: any,
  gates?: any,
  defaultVehicleType?: any,
  defaultLoadType?: any
) {
  const currentDate = moment()
  const currentTime = currentDate.format('HH:mm')

  const initialLocation = assetData?.gate?.siteId
    ? {
        label: assetData?.gate?.site?.name,
        value: assetData?.gate?.siteId,
      }
    : {
        label: sites?.[0]?.label,
        value: sites?.[0]?.value,
      }

  const initialGate = assetData?.gate?.id
    ? {
        label: assetData?.gate?.name,
        value: assetData?.gate?.id,
      }
    : {
        label: gates?.[0]?.label,
        value: gates?.[0]?.value,
      }

  const initialDirection = assetData?.direction
    ? { label: 'Ingate', value: 'ingate' }
    : { label: 'Outgate', value: 'outgate' }

  const initialCarrierData = assetData?.vehicle?.carrier
    ? {
        label: assetData?.vehicle?.carrier?.name,
        value: assetData?.vehicle?.carrier?.id,
      }
    : undefined

  const initialWeightClass = assetData?.vehicle?.weightClass
    ? {
        label: assetData?.vehicle?.weightClass,
        value: assetData?.vehicle?.weightClass,
      }
    : undefined

  const initialFuelType = assetData?.vehicle?.fuelType
    ? {
        label: assetData?.vehicle?.fuelType,
        value: assetData?.vehicle?.fuelType,
      }
    : undefined

  const initialLoadType = assetData?.load?.type?.name
    ? {
        label: assetData?.load?.type?.name,
        value: assetData?.load?.type?.id,
      }
    : undefined

  const initialValues = {
    location: initialLocation,
    gate: initialGate,
    loadNumber: assetData?.load?.number || '',
    assetStatus: assetData?.direction === false ? 'outgated' : 'in-the-yard',
    ingateDate: assetData?.detectedAt || currentDate.toISOString(),
    outgateDate: assetData?.updatedAt || currentDate.toISOString(),
    ingateTime: assetData?.detectedAt ? getFormTime(assetData.detectedAt) : currentTime,
    outgateTime: assetData?.updatedAt ? getFormTime(assetData.updatedAt) : currentTime,
    loadType: initialLoadType,
    sealPresent: assetData?.hasSeal || false,
    sealNumber: assetData?.sealNumber || '',
    loadLPNumber: assetData?.load?.licensePlate || '',
    bco: assetData?.load?.bco || '',
    loadRemarks: assetData?.loadRemarks || '',
    chassisNumber: assetData?.chassis?.number || '',
    chassisLPNumber: assetData?.chassis?.licensePlate || '',
    chassisRemarks: assetData?.chassisRemarks || '',
    vehicleType: {
      label: assetData?.vehicle?.type?.name || defaultVehicleType?.label,
      value: assetData?.vehicle?.type?.id || defaultVehicleType?.value,
    },
    carrier: initialCarrierData,
    truckNumber: assetData?.vehicle?.number || '',
    truckDOT: assetData?.vehicle?.dotNumber || '',
    truckLPNumber: assetData?.vehicle?.licensePlate || '',
    truckClass: initialWeightClass,
    fuelType: initialFuelType,
    vehicleRemarks: assetData?.vehicleRemarks || '',
    direction: initialDirection,
  }

  return initialValues
}

export function getFormTime(time: string) {
  return moment(time).format('HH:mm')
}

export function getSelectOptions(values = {}) {
  return Object.keys(values).map((key) => ({
    label: values[key].name,
    value: values[key].id,
  }))
}

export function hasAttachmentData(attachmentData) {
  return attachmentData.some((attachment) => attachment.assetName !== 'Tractor')
}

export function getAttachmentDetails(attachment) {
  if (attachment.assetName === 'Tractor') {
    return null
  }
  if (attachment.assetName === 'Chassis') {
    return `Chassis - ${attachment.chassisNumber || 'Number not provided'}`
  }
  return `${attachment.loadType} - ${attachment.loadNumber || 'Number not provided'}`
}

export function getOutgateEvent(outwardObservation, assetType) {
  return outwardObservation ? `${assetType} - ${formatTime(outwardObservation.detectedAt)}` : null
}

export function getInitialDateFilter() {
  const currentDate = new Date()
  const oneWeekDifference = DateHelper().subtract('day', 7).toString()

  return {
    startDate: startOfDayUTC(new Date(oneWeekDifference)),
    endDate: endOfDayUTC(currentDate),
  }
}
